import { isInternalUser, isYardDriverUser } from '@nova/core';
import routeHelpers from '@satellite/helpers/routes';
import {
  completeMcpPortalLogin,
  readMcpTransaction,
  withMcpTransactionQuery
} from '@satellite/helpers/mcp-login-bridge';
import { useStore } from 'vuex';
import config from '@/config/config';
import { isEnabled } from '@/utils';

/**
 * Only allow same-origin relative paths (reject protocol-relative // and absolute URLs).
 * @param {unknown} redirectPath
 * @returns {string|null}
 */
export function sanitizeRedirectPath(redirectPath) {
  if (typeof redirectPath !== 'string' || redirectPath.length === 0) {
    return null;
  }
  if (!redirectPath.startsWith('/') || redirectPath.startsWith('//')) {
    return null;
  }
  return redirectPath;
}

/**
 * Parses the `nextRoute` query value into a route location. Anything that is not a JSON object with
 * a string `name` resolves to the appointments route.
 * @param {unknown} rawNextRoute
 * @returns {{ name: string }}
 */
export function parseNextRoute(rawNextRoute) {
  const fallback = { name: 'appointments' };
  if (typeof rawNextRoute !== 'string' || rawNextRoute.length === 0) {
    return fallback;
  }
  try {
    const parsed = JSON.parse(rawNextRoute);
    return parsed && typeof parsed === 'object' && typeof parsed.name === 'string'
      ? parsed
      : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Mirror LoginForm watcher destination once the user is known to be authenticated.
 * @param {{ redirectPath?: unknown, me: object, defaultLoginPath?: string }} params
 * @returns {{ path: string } | { name: string }}
 */
export function resolveAuthenticatedLoginDestination({
  redirectPath,
  me,
  defaultLoginPath = 'appointments'
}) {
  const safePath = sanitizeRedirectPath(redirectPath);
  if (safePath && safePath !== '/login' && safePath !== '/') {
    return { path: safePath };
  }

  if (isInternalUser(me)) {
    return { name: 'internalHome' };
  }

  return { name: defaultLoginPath };
}

/**
 * Sends a yard driver to Circe, with a handoff code when enable-handoff-helios-to-circe is on and
 * the legacy ?token= redirect otherwise.
 * @param {{ user: object, accessToken?: string | null }} params
 * @returns {Promise<boolean>} true when a redirect occurred
 */
export function hopYardDriverToCirce({ user, accessToken }) {
  return routeHelpers.redirectYardDriverToCirce({
    user,
    circeUrl: config.circe_url,
    accessToken,
    useHandoffCode: isEnabled('enable-handoff-helios-to-circe')
  });
}

/**
 * With mcp_txn present, complete MCP from Helios for carriers and warehouse users.
 * Yard drivers still go to Circe (no MCP bridge there).
 * @param {{ me: object, mcpTransaction: string | null }} params
 * @returns {Promise<boolean>}
 */
export async function tryCompleteOrHopMcpLogin({ me, mcpTransaction }) {
  if (!mcpTransaction || !me) {
    return false;
  }

  if (isYardDriverUser(me)) {
    return hopYardDriverToCirce({ user: me });
  }

  return completeMcpPortalLogin({
    mcpTransaction,
    mcpUrl: config.mcp_url
  });
}

/**
 * Skip the login form when a valid session already exists.
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} _from
 * @param {import('vue-router').NavigationGuardNext} next
 */
export async function loginBeforeEnter(to, _from, next) {
  // Email verification lands on /login/:verificationToken and must reach LoginForm
  // so Auth/verifyEmail can run — even if a leftover session is still authorized.
  if (to.params?.verificationToken) {
    next();
    return;
  }

  const store = useStore();
  const mcpTransaction = readMcpTransaction(to.query);

  if (await routeHelpers.isUserAuthorized(store)) {
    const me = store.getters['Auth/me'];
    if (await tryCompleteOrHopMcpLogin({ me, mcpTransaction })) {
      return;
    }

    next(
      withMcpTransactionQuery(
        resolveAuthenticatedLoginDestination({
          redirectPath: to.query.redirectPath,
          me
        }),
        mcpTransaction
      )
    );
    return;
  }

  next();
}
